export interface TabFileState {
  path: string;
  isComponent: boolean;
  sourceContent: string;
  hasUnsavedChanges: boolean;
  lastSavedHash: string | null;
  lastSaveTime: number | null;
  userVariables: Record<string, string>;
  staticVariables: Record<string, string>;
  dynamicVariables: Record<string, string>;
  editorScrollTop: number;
  editorCursorPos: number;
  activeScriptTab: string | null;
  checklistEnabled: boolean;
  checklistSlugs: string[];
  /**
   * Per-tab CodeMirror state, including its own undo/redo history.
   * Typed as `unknown` here to avoid a CodeMirror dependency in this module;
   * the client treats it as an `EditorState`.
   */
  editorState?: unknown;
}

export interface TabPersistence {
  tabs: string[];
  active: string | null;
}

export class TabManager {
  private openTabs: Map<string, TabFileState> = new Map();
  private _tabOrder: string[] = [];
  /** Fast lookup set kept in sync with _tabOrder to enforce uniqueness */
  private _tabSet: Set<string> = new Set();
  private _pinnedPaths: Set<string> = new Set();
  private _activeTab: string | null = null;

  /** Callback fired whenever tabs change (open/close/reorder/activate) */
  onChange: (() => void) | null = null;

  get activeTab(): string | null {
    return this._activeTab;
  }

  /** Tabs in display order: pinned first (in pin insertion order), then the rest. */
  get tabs(): TabFileState[] {
    const pinned = this._tabOrder.filter(p => this._pinnedPaths.has(p));
    const regular = this._tabOrder.filter(p => !this._pinnedPaths.has(p));
    return [...pinned, ...regular].map(p => this.openTabs.get(p)!).filter(Boolean);
  }

  get tabOrder(): string[] {
    return [...this._tabOrder];
  }

  /** Mark a path as pinned. Pinned tabs cannot be closed and sort to the front. */
  pinPath(path: string): void {
    if (this._pinnedPaths.has(path)) return;
    this._pinnedPaths.add(path);
    this.onChange?.();
  }

  unpinPath(path: string): void {
    if (!this._pinnedPaths.delete(path)) return;
    this.onChange?.();
  }

  isPinned(path: string): boolean {
    return this._pinnedPaths.has(path);
  }

  /**
   * Add a tab as a non-active placeholder. Used for pinned tabs that should
   * appear in the bar even before the user has visited them. The content is
   * loaded on first click (same lazy-load path as restored tabs).
   */
  addPinnedPlaceholder(path: string): void {
    this.pinPath(path);
    if (this._tabSet.has(path)) return;
    this._tabOrder.push(path);
    this._tabSet.add(path);
    this.openTabs.set(path, this.makePlaceholderState(path));
    this.onChange?.();
  }

  private makePlaceholderState(path: string): TabFileState {
    return {
      path,
      isComponent: path.startsWith('_components/'),
      sourceContent: '',
      hasUnsavedChanges: false,
      lastSavedHash: null,
      lastSaveTime: null,
      userVariables: {},
      staticVariables: {},
      dynamicVariables: {},
      editorScrollTop: 0,
      editorCursorPos: 0,
      activeScriptTab: null,
      checklistEnabled: false,
      checklistSlugs: [],
    };
  }

  get tabCount(): number {
    return this._tabOrder.length;
  }

  /**
   * Open a file as a tab.
   * If the path is already open, updates its state without creating a duplicate.
   * Returns the tab state.
   */
  openTab(path: string, state: TabFileState): TabFileState {
    if (!this._tabSet.has(path)) {
      this._tabOrder.push(path);
      this._tabSet.add(path);
    }
    this.openTabs.set(path, state);
    this._activeTab = path;
    this.onChange?.();
    return state;
  }

  /**
   * Switch active tab. Does NOT load from server - caller handles restore.
   * Returns the tab state, or null if tab not found.
   */
  activateTab(path: string): TabFileState | null {
    const state = this.openTabs.get(path);
    if (!state) return null;
    this._activeTab = path;
    this.onChange?.();
    return state;
  }

  /** Get the current active tab state */
  getActiveState(): TabFileState | null {
    if (!this._activeTab) return null;
    return this.openTabs.get(this._activeTab) ?? null;
  }

  /** Update the state snapshot for the currently active tab */
  updateActiveState(partial: Partial<TabFileState>): void {
    if (!this._activeTab) return;
    const existing = this.openTabs.get(this._activeTab);
    if (!existing) return;
    this.openTabs.set(this._activeTab, { ...existing, ...partial });
  }

  /** Update the state snapshot for any open tab (no-op if not open) */
  updateTabState(path: string, partial: Partial<TabFileState>): void {
    const existing = this.openTabs.get(path);
    if (!existing) return;
    this.openTabs.set(path, { ...existing, ...partial });
  }

  /**
   * Close a tab. Returns the path of the tab to activate next,
   * or null if no tabs remain.
   * Pinned tabs cannot be closed; the call is a no-op for them.
   */
  closeTab(path: string): string | null {
    if (!this.openTabs.has(path)) return this._activeTab;
    if (this._pinnedPaths.has(path)) return this._activeTab;

    const idx = this._tabOrder.indexOf(path);
    this.openTabs.delete(path);
    this._setOrder(this._tabOrder.filter(p => p !== path));

    if (this._activeTab === path) {
      // Pick adjacent tab: prefer right, then left
      if (this._tabOrder.length === 0) {
        this._activeTab = null;
      } else {
        const newIdx = Math.min(idx, this._tabOrder.length - 1);
        this._activeTab = this._tabOrder[newIdx];
      }
    }

    this.onChange?.();
    return this._activeTab;
  }

  /** Close all tabs except the given path (and any pinned tabs). Returns paths of closed tabs. */
  closeAllExcept(path: string): string[] {
    const keep = new Set<string>([path, ...this._pinnedPaths]);
    const closed = this._tabOrder.filter(p => !keep.has(p));
    for (const p of closed) {
      this.openTabs.delete(p);
    }
    this._setOrder(this._tabOrder.filter(p => keep.has(p) && this.openTabs.has(p)));
    this._activeTab = this.openTabs.has(path) ? path : (this._tabOrder[0] ?? null);
    this.onChange?.();
    return closed;
  }

  /** Check if a path is open as a tab */
  isOpen(path: string): boolean {
    return this._tabSet.has(path);
  }

  /** Get a tab's state by path */
  getTab(path: string): TabFileState | null {
    return this.openTabs.get(path) ?? null;
  }

  /** Remove a tab (used when file deleted externally) */
  removeTab(path: string): void {
    this.closeTab(path);
  }

  /**
   * Rename a tab (update path key). Used when a file is renamed externally.
   * Returns true if the tab was found and renamed, false otherwise.
   */
  renameTab(oldPath: string, newPath: string): boolean {
    const state = this.openTabs.get(oldPath);
    if (!state) return false;

    // Update state object
    state.path = newPath;
    state.isComponent = newPath.startsWith('_components/');

    // Update map: remove old key, add new key
    this.openTabs.delete(oldPath);
    this.openTabs.set(newPath, state);

    // Update order array and set
    const idx = this._tabOrder.indexOf(oldPath);
    if (idx !== -1) {
      this._tabOrder[idx] = newPath;
    }
    this._tabSet.delete(oldPath);
    this._tabSet.add(newPath);

    // Update active tab reference if needed
    if (this._activeTab === oldPath) {
      this._activeTab = newPath;
    }

    this.onChange?.();
    return true;
  }

  /**
   * Move a tab so it sits before (or after) another tab. Used by
   * drag-and-drop reordering in the tab bar.
   * `targetPath === null` moves the tab to the end.
   * Note: the `tabs` getter still displays pinned tabs first, so a regular
   * tab dropped before a pinned one lands right after the pinned group.
   */
  moveTab(path: string, targetPath: string | null, before = true): boolean {
    if (!this._tabSet.has(path)) return false;
    if (targetPath !== null && (targetPath === path || !this._tabSet.has(targetPath))) return false;

    const order = this._tabOrder.filter(p => p !== path);
    if (targetPath === null) {
      order.push(path);
    } else {
      const idx = order.indexOf(targetPath);
      order.splice(before ? idx : idx + 1, 0, path);
    }

    // No-op moves (dropping a tab back onto its own position) shouldn't
    // trigger re-render/persist churn
    if (order.every((p, i) => p === this._tabOrder[i])) return false;

    this._setOrder(order);
    this.onChange?.();
    return true;
  }

  /** Close all tabs. Pinned tabs are reset to placeholder state but kept in the bar. */
  closeAll(): void {
    const pinned = [...this._pinnedPaths];
    this.openTabs.clear();
    this._setOrder([]);
    for (const path of pinned) {
      this._tabOrder.push(path);
      this._tabSet.add(path);
      this.openTabs.set(path, this.makePlaceholderState(path));
    }
    this._activeTab = null;
    this.onChange?.();
  }

  /** Get the next tab path (wraps around). Returns null if fewer than 2 tabs. */
  nextTab(): string | null {
    if (this._tabOrder.length < 2 || !this._activeTab) return null;
    const idx = this._tabOrder.indexOf(this._activeTab);
    return this._tabOrder[(idx + 1) % this._tabOrder.length];
  }

  /** Get the previous tab path (wraps around). Returns null if fewer than 2 tabs. */
  prevTab(): string | null {
    if (this._tabOrder.length < 2 || !this._activeTab) return null;
    const idx = this._tabOrder.indexOf(this._activeTab);
    return this._tabOrder[(idx - 1 + this._tabOrder.length) % this._tabOrder.length];
  }

  /** Serialize open tabs for localStorage persistence */
  serialize(): TabPersistence {
    return {
      tabs: [...this._tabOrder],
      active: this._activeTab,
    };
  }

  /** Restore tab order from serialized state (content loaded lazily on activate). Pinned tabs are preserved. */
  restoreFromPaths(paths: string[], activePath: string | null): void {
    // Snapshot existing pinned tabs (preserve their state if any)
    const pinnedToRestore = this._tabOrder.filter(p => this._pinnedPaths.has(p));
    const pinnedStates = new Map<string, TabFileState>();
    for (const p of pinnedToRestore) {
      const state = this.openTabs.get(p);
      if (state) pinnedStates.set(p, state);
    }

    this.openTabs.clear();
    this._setOrder([]);

    // Re-add pinned tabs first
    for (const path of pinnedToRestore) {
      const state = pinnedStates.get(path) ?? this.makePlaceholderState(path);
      this.openTabs.set(path, state);
      this._tabOrder.push(path);
      this._tabSet.add(path);
    }

    for (const path of paths) {
      // Skip duplicates (including pinned)
      if (this._tabSet.has(path)) continue;

      this.openTabs.set(path, this.makePlaceholderState(path));
      this._tabOrder.push(path);
      this._tabSet.add(path);
    }

    this._activeTab = activePath && this.openTabs.has(activePath) ? activePath : null;
    this.onChange?.();
  }

  /**
   * Replace _tabOrder and rebuild _tabSet to stay in sync.
   * All mutations to _tabOrder should go through this method or
   * use the push+add pattern for single appends.
   */
  private _setOrder(order: string[]): void {
    this._tabOrder = order;
    this._tabSet = new Set(order);
  }
}
